import { AmcLogo } from "@core/components/logo/amc-logo";
import {
  V2Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  VisuallyHidden,
  Textarea,
} from "web-design-system";
import { SUBMITTED_ORDER_TYPE_LABELS } from "creso-core/constants";
import { Client, SubmittedOrder } from "creso-core/types";
import { format } from "date-fns";
import { useCancelSubmittedOrder } from "creso-core/hooks";
import { handleError } from "@/utils/error";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { convertToCurrency } from "creso-core/utils";
import Units from "@funds/components/atoms/units";
import { Loader2 } from "lucide-react";

type Props = {
  client: Client;
  order: SubmittedOrder;
  open: boolean;
  toggle: (show: boolean) => void;
};

// Allow only alphanumeric input (a-z, A-Z, 0-9) plus whitespace.
// If the string fully matches this pattern, it is considered valid.
const TEXT_CHAR_REGEX = /^[a-zA-Z0-9\s]*$/;

function OrderSummary({ order }: { order: SubmittedOrder }) {
  return (
    <>
      <div className="flex flex-col gap-1 flex-1">
        <span className="text-xs text-muted-foreground">Amount / units</span>
        <span className="text-sm font-semibold">
          {order.amount && order.units ? (
            <>
              {convertToCurrency(order.amount)}
              <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                <Units>{order.units}</Units>
              </span>
            </>
          ) : order.amount ? (
            convertToCurrency(order.amount)
          ) : order.units ? (
            <Units>{order.units}</Units>
          ) : (
            "-"
          )}
        </span>
      </div>
      <div className="flex flex-col gap-1 flex-1">
        <span className="text-xs text-muted-foreground">Placed on</span>
        <span className="text-sm font-semibold">
          {format(new Date(order.created_at), "dd MMM yyyy")}
        </span>
      </div>
    </>
  );
}

export function CancelSubmittedOrderDialog(props: Props) {
  const { mutateAsync: cancelSubmittedOrder, isPending: isCancelling } =
    useCancelSubmittedOrder();
  const { order, client } = props;

  const [reason, setReason] = useState<string>("");

  useEffect(() => {
    return () => {
      setReason("");
    };
  }, [props.open]);

  const orderTypeLabel = SUBMITTED_ORDER_TYPE_LABELS[order.type];

  const handleCancel = async () => {
    if (isCancelling) return;
    try {
      if (reason && !TEXT_CHAR_REGEX.test(reason)) {
        toast.error("Reason can only contain text (no emojis or symbols).");
        return;
      }

      const trimmed = reason.trim();
      await cancelSubmittedOrder({
        id: order.id,
        investment_account_guid: client.investment_account ?? "",
        ...(trimmed ? { remarks: trimmed } : {}),
      });
      toast.success("Order cancelled successfully");
      props.toggle(false);
    } catch (error) {
      handleError(error, "Failed to cancel order");
    }
  };

  return (
    <V2Dialog open={props.open} onOpenChange={props.toggle}>
      <DialogContent className="p-6 gap-6 space-y-0 w-100">
        <DialogHeader>
          <VisuallyHidden>
            <DialogTitle className="text-lg">
              Confirm {orderTypeLabel} cancellation?
            </DialogTitle>
          </VisuallyHidden>
          <div className="flex flex-col">
            <div className="text-lg font-semibold">
              Confirm {orderTypeLabel} cancellation
            </div>
            <div className="p-3 mt-3 bg-muted flex flex-col rounded-lg">
              <AmcLogo amc_code={order.amc_morningstar_code} />
              <span className="text-sm font-semibold mt-2">
                {order.scheme_name}
              </span>
              <div className="flex flex-row w-full mt-5">
                <OrderSummary order={order} />
              </div>
            </div>
            <Textarea
              className="mt-3"
              placeholder="Enter reason for cancelling (optional)"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <span className="text-sm mt-3 text-muted-foreground">
              This cancellation is processed immediately and does not require
              client authorisation.
            </span>
          </div>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            disabled={isCancelling}
            onClick={() => props.toggle(false)}
          >
            No
          </Button>
          <Button onClick={handleCancel} disabled={isCancelling}>
            {isCancelling ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              "Proceed"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </V2Dialog>
  );
}
